import { tt } from './i18n'

/**
 * How a payment method is drawn - `FRONTEND.md` §9.204, moved here by §9.384.
 *
 * **Two screens were naming the same six methods.** This lived in `manager-delivery`'s dispatch
 * rules, and the driver app had its own `paymentLabel` - different words, no colour - because
 * `manager-driving` cannot import `manager-delivery`. The owner asked for the driver's badge to
 * *"match colors from Rozvoz nástenka"*, which is a request for one vocabulary and not for a second
 * set of tones. Re-exported from `dispatch-rules.ts`, so every existing call site and its tests are
 * unchanged.
 */
/**
 * Which icon a payment method takes on the board — §9.204.
 *
 * A *kind*, not a component: this file is pure and tested as such, and the call site maps the two
 * strings to the icons the payment dialog already uses, so the board and the till agree.
 *
 * `other` is a card rather than nothing — every method here that is not cash is taken on a machine,
 * and an unrecognised one is far more likely to be a new card type than a new way of handing over
 * notes.
 */
export type PaymentIcon = 'cash' | 'card'

export function paymentMethodBadge(
  method: string | undefined,
): { label: string; tone: string; icon: PaymentIcon } | null {
  if (method === undefined || method === '') return null
  const known: Record<string, { label: string; tone: string; icon: PaymentIcon }> = {
    cash: {
      label: tt('Hotovosť'),
      tone: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
      icon: 'cash',
    },
    card_terminal: {
      label: tt('Karta – terminál'),
      tone: 'bg-red-500/15 text-red-400 border-red-500/30',
      icon: 'card',
    },
    card_courier: {
      label: tt('Karta kuriér'),
      tone: 'bg-muted/40 text-muted-foreground border-transparent',
      icon: 'card',
    },
    card_online: {
      label: 'CardPay online',
      tone: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
      icon: 'card',
    },
    card: { label: tt('Karta'), tone: 'bg-red-500/15 text-red-400 border-red-500/30', icon: 'card' },
    other: {
      label: tt('Iné'),
      tone: 'bg-muted/40 text-muted-foreground border-transparent',
      icon: 'card',
    },
  }
  return (
    known[method] ?? {
      label: method,
      tone: 'bg-muted/40 text-muted-foreground border-transparent',
      icon: 'card',
    }
  )
}
